import { useState, useEffect } from "react";
import { Menu } from "antd";
import { NavLink, useLocation } from "react-router-dom";
import styled from "styled-components";
import { hasPermission } from "../permissions";
import {
  BarChartOutlined, GiftOutlined, PhoneOutlined, CalendarOutlined, FileTextOutlined,
  AppstoreOutlined, TeamOutlined,
  ToolOutlined, CheckSquareOutlined, PlusCircleOutlined,
  BellOutlined, NotificationOutlined, ReadOutlined, ContainerOutlined,
  BankOutlined, PieChartOutlined, AuditOutlined, StarOutlined,
  FormOutlined, SolutionOutlined, BookOutlined,
  SafetyOutlined, IdcardOutlined, FileImageOutlined,
  DashboardOutlined, EnvironmentOutlined, FundOutlined,
  ClockCircleOutlined, GlobalOutlined, HomeOutlined, UserSwitchOutlined,
  MessageOutlined, SettingOutlined, ShopOutlined, UnorderedListOutlined, SlidersOutlined,
  MobileOutlined, CustomerServiceOutlined, HeartOutlined, BulbOutlined,
  LinkOutlined, TrophyOutlined, ApartmentOutlined, PictureOutlined,
} from "@ant-design/icons";

const { SubMenu } = Menu;

// ─── Section titles & icons ───────────────────────────────────────────────────
const GROUP_META = {
  reports:              { title: 'Reports',               icon: BarChartOutlined   },
  'member-services':    { title: 'Member Services',        icon: AppstoreOutlined   },
  'work-comm':          { title: 'Work & Communication',   icon: ToolOutlined       },
  forms:                { title: 'Forms',                  icon: FormOutlined       },
  community:            { title: 'Community',              icon: TeamOutlined       },
  administration:       { title: 'Manage',                 icon: SafetyOutlined     },
  settings:             { title: 'Settings',               icon: SettingOutlined    },
  'data-directory':     { title: 'Directory',              icon: AppstoreOutlined   },
  population:           { title: 'Population',             icon: GlobalOutlined     },
  'business-directory': { title: 'Business Directory',     icon: ShopOutlined       },
};

// ─── Styled sidenav menu ──────────────────────────────────────────────────────
const StyledMenu = styled(Menu)`
  border: none !important;
  background: transparent !important;

  /* Every menu item */
  .ant-menu-item {
    height: 42px !important;
    line-height: 42px !important;
    margin: 2px 8px !important;
    width: calc(100% - 16px) !important;
    border-radius: 8px !important;
    padding-left: 16px !important;
    font-size: 13.5px !important;
    font-weight: 500 !important;
    color: rgba(255,255,255,0.75) !important;
    transition: background 0.15s, color 0.15s !important;

    &:hover {
      background: rgba(255,255,255,0.12) !important;
      color: #fff !important;
    }

    /* active selected */
    &.ant-menu-item-selected {
      background: rgba(255,215,0,0.15) !important;
      color: #FFD700 !important;
      font-weight: 700 !important;
      border-left: 3px solid #FFD700 !important;
      padding-left: 13px !important;
      &::after { border-right: none !important; }
    }

    a, a:hover, a:active, a:focus {
      color: inherit !important;
      text-decoration: none !important;
    }
  }

  /* Sub-menu title row */
  .ant-menu-submenu-title {
    height: 42px !important;
    line-height: 42px !important;
    margin: 2px 8px !important;
    width: calc(100% - 16px) !important;
    border-radius: 8px !important;
    padding-left: 16px !important;
    font-size: 13.5px !important;
    font-weight: 500 !important;
    color: rgba(255,255,255,0.75) !important;
    transition: background 0.15s, color 0.15s !important;

    &:hover {
      background: rgba(255,255,255,0.12) !important;
      color: #fff !important;
    }

    .anticon { font-size: 15px !important; color: rgba(255,255,255,0.75) !important; }
    .ant-menu-submenu-arrow { color: rgba(255,255,255,0.5) !important; }
  }

  /* Submenu inline background */
  .ant-menu-sub {
    background: rgba(0,0,0,0.15) !important;
    max-height: none !important;
    overflow: visible !important;
  }

  /* Nested items inside submenu */
  .ant-menu-sub .ant-menu-item {
    padding-left: 28px !important;
    font-size: 13px !important;
    height: 38px !important;
    line-height: 38px !important;
    color: rgba(255,255,255,0.7) !important;
    display: flex !important;
    align-items: center !important;
  }

  /* Selected nested item */
  .ant-menu-sub .ant-menu-item.ant-menu-item-selected,
  .ant-menu-sub .ant-menu-item-selected {
    background: rgba(255,215,0,0.12) !important;
    color: #FFD700 !important;
    font-weight: 700 !important;
    border-left: 3px solid #FFD700 !important;
    padding-left: 25px !important;
  }

  /* Remove the default left inline indicator */
  &.ant-menu-inline .ant-menu-item::after {
    border-right: none !important;
  }
`;

// ─── Section header ───────────────────────────────────────────────────────────
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 16px 10px 18px;
  font-size: 11px;
  font-weight: 700;
  color: rgba(255,255,255,0.45);
  text-transform: uppercase;
  letter-spacing: 0.9px;
  border-left: 3px solid rgba(255,255,255,0.25);
  margin-left: 8px;
  margin-bottom: 4px;

  .sec-icon {
    font-size: 14px;
    color: rgba(255,255,255,0.45);
    flex-shrink: 0;
  }
`;

// ─── Divider label for mobile "show-all" sections ─────────────────────────────
const MobileSectionLabel = styled.div`
  padding: 14px 16px 6px;
  font-size: 11px;
  font-weight: 700;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 7px;

  .sec-icon { font-size: 13px; color: #1890ff; }
`;

// ─── Helper: derive selectedKeys from pathname ────────────────────────────────
function getSelectedKey(pathname) {
  const p = pathname;
  // Reports
  if (p === '/reports/manual-report')   return 'manual-report';
  if (p === '/reports/birthday-list')   return 'birthday-list';
  if (p === '/reports/calling-report')  return 'calling-report';
  if (p.startsWith('/calling-module'))  return 'calling-module';
  if (p === '/reports/yearly-report')      return 'yearly-report';
  if (p === '/reports/payment-continuity') return 'payment-continuity';
  if (p === '/reports/transaction-summary') return 'transaction-summary';
  if (p === '/reports/referral-report') return 'referral-report';
  if (p === '/reports/geo-report')      return 'geo-report';
  if (p === '/reports/calendar-report') return 'calendar-report';
  // Member services
  if (p.startsWith('/vishesh-sahyogi')) return 'vishesh-sahyogi';
  if (p.startsWith('/visitor'))         return 'visitor';
  if (p === '/village/bulk-correction')  return 'village-bulk-correction';
  if (p.startsWith('/village'))         return 'village';
  // Work-comm
  if (p.startsWith('/work_managment'))  return 'work_managment';
  if (p.startsWith('/work-tracking/team-dashboard')) return 'work-tracking-team';
  if (p.startsWith('/work-tracking/add-activity'))   return 'work-tracking-add';
  if (p.startsWith('/work-tracking'))   return 'work-tracking';
  if (p.startsWith('/inquiry'))         return 'inquiry';
  if (p.startsWith('/contributions'))   return 'contributions';
  if (p.startsWith('/festivals'))       return 'festivals';
  if (p.startsWith('/admin-status'))    return 'admin-status';
  if (p.startsWith('/daily-quotes'))    return 'daily-quotes';
  if (p.startsWith('/music-library'))   return 'music-library';
  if (p.startsWith('/gallery'))         return 'gallery';
  if (p.startsWith('/jobs-admin'))               return 'jobs-admin';
  if (p.startsWith('/vivah-admin'))              return 'vivah-admin';
  if (p.startsWith('/samaj-parichay'))           return 'samaj-parichay';
  if (p.startsWith('/sammelan'))                 return 'sammelan';
  if (p.startsWith('/gram-pramukh'))             return 'gram-pramukh';
  if (p.startsWith('/gotra-directory'))          return 'gotra-directory';
  if (p.startsWith('/vishesh-sahyogi'))          return 'vishesh-sahyogi';
  if (p.startsWith('/comp-exam'))                return 'comp-exam';
  if (p.startsWith('/student-support'))          return 'student-support';
  if (p.startsWith('/message-log'))              return 'message-log';
  if (p.startsWith('/payment-reminder-rules'))   return 'payment-reminder-rules';
  if (p.startsWith('/bank-account'))             return 'bank-account';
  if (p.startsWith('/special-occasion'))         return 'special-occasion';
  if (p.startsWith('/receipt-template'))         return 'receipt-template';
  if (p.startsWith('/festival-campaign')) return 'festival-campaign';
  if (p.startsWith('/staff-chat'))        return 'staff-chat';
  if (p.startsWith('/app-settings'))      return 'app-settings';
  // Financial
  if (p.startsWith('/expenditure-mgmt'))  return 'expenditure-mgmt';
  if (p.startsWith('/financial-reports')) return 'financial-reports';
  if (p.startsWith('/beneficiaries'))   return 'expenditure-mgmt';
  if (p.startsWith('/purposes'))        return 'expenditure-mgmt';
  if (p.startsWith('/expenditures'))    return 'expenditure-mgmt';
  if (p.startsWith('/budget-allocations')) return 'expenditure-mgmt';
  if (p.startsWith('/approval-workflow'))  return 'expenditure-mgmt';
  if (p.startsWith('/reports/'))        return 'financial-reports';
  if (p.startsWith('/monthly-reports')) return 'financial-reports';
  if (p.startsWith('/portal-updates'))               return 'portal-updates';
  if (p.startsWith('/admin-notifications'))          return 'admin-notifications';
  if (p.startsWith('/community-board'))              return 'community-board';
  // Forms
  if (p.startsWith('/registration'))   return 'registration';
  if (p.startsWith('/purnima-form'))   return 'purnima-form';
  if (p.startsWith('/announced'))      return 'announced';
  if (p.startsWith('/comp-exam'))      return 'comp-exam';
  if (p.startsWith('/poster'))         return 'poster';
  // Admin
  if (p.startsWith('/staff-role'))             return 'staff-role';
  if (p.startsWith('/role'))                   return 'staff-role';
  if (p.startsWith('/staff-tracking/admin'))   return 'staff-tracking-admin';
  if (p.startsWith('/staff-tracking'))         return 'staff-tracking';
  if (p.startsWith('/staff'))                  return 'staff-role';
  if (p.startsWith('/tracking'))               return 'tracking';
  if (p.startsWith('/trash'))                  return 'trash-payments';
  // Population
  if (p.startsWith('/pop-dashboard'))      return 'pop-dashboard';
  if (p.startsWith('/pop-education'))      return 'pop-education';
  if (p.startsWith('/pop-occupations'))    return 'pop-occupations';
  if (p.startsWith('/pop-country'))        return 'pop-country';
  if (p.startsWith('/pop-states'))         return 'pop-states';
  if (p.startsWith('/pop-districts'))      return 'pop-districts';
  if (p.startsWith('/pop-tehsils'))        return 'pop-tehsils';
  if (p.startsWith('/pop-panchayat'))      return 'pop-panchayat';
  if (p.startsWith('/pop-village'))        return 'pop-village';
  if (p.startsWith('/pop-families'))       return 'pop-families';
  if (p.startsWith('/pop-family-member'))  return 'pop-family-member';
  // Business Directory
  if (p.startsWith('/business-directory/sectors'))  return 'bd-sectors';
  if (p.startsWith('/business-directory/config'))   return 'bd-config';
  if (p.startsWith('/business-directory'))          return 'bd-list';
  return '';
}

// ─── Active item style helper ─────────────────────────────────────────────────
const activeItemStyle = {
  background: 'rgba(255,215,0,0.15)',
  borderLeft: '3px solid #FFD700',
  fontWeight: 700,
  paddingLeft: 13,
  color: '#FFD700',
};

// ─── Sub-item renderers ───────────────────────────────────────────────────────

function ReportsItems() {
  const { pathname } = useLocation();
  const is = (path) => pathname === path;
  return (
    <>
      {hasPermission('/reports/manual-report',  'readPermission') && <Menu.Item key="manual-report"  icon={<FileTextOutlined />} style={is('/reports/manual-report')  ? activeItemStyle : {}}><NavLink to="/reports/manual-report">Manually Report</NavLink></Menu.Item>}
      {hasPermission('/reports/birthday-list',  'readPermission') && <Menu.Item key="birthday-list"  icon={<GiftOutlined />}     style={is('/reports/birthday-list')   ? activeItemStyle : {}}><NavLink to="/reports/birthday-list">Birthday Module</NavLink></Menu.Item>}
      {hasPermission('/reports/calling-report', 'readPermission') && <Menu.Item key="calling-report" icon={<PhoneOutlined />}    style={is('/reports/calling-report')  ? activeItemStyle : {}}><NavLink to="/reports/calling-report">Calling Module</NavLink></Menu.Item>}
      {hasPermission('/reports/yearly-report',  'readPermission') && <Menu.Item key="yearly-report"      icon={<BarChartOutlined />} style={is('/reports/yearly-report')      ? activeItemStyle : {}}><NavLink to="/reports/yearly-report">Yearly Report</NavLink></Menu.Item>}
      {hasPermission('/reports/yearly-report',  'readPermission') && <Menu.Item key="payment-continuity" icon={<BarChartOutlined />} style={is('/reports/payment-continuity') ? activeItemStyle : {}}><NavLink to="/reports/payment-continuity">Payment Continuity</NavLink></Menu.Item>}
      <Menu.Item key="transaction-summary" icon={<AuditOutlined />}   style={is('/reports/transaction-summary') ? activeItemStyle : {}}><NavLink to="/reports/transaction-summary">Transaction Summary</NavLink></Menu.Item>
      <Menu.Item key="referral-report"     icon={<TeamOutlined />}    style={is('/reports/referral-report')     ? activeItemStyle : {}}><NavLink to="/reports/referral-report">Referral Report</NavLink></Menu.Item>
      <Menu.Item key="geo-report"          icon={<EnvironmentOutlined />} style={is('/reports/geo-report')      ? activeItemStyle : {}}><NavLink to="/reports/geo-report">Geographic Report</NavLink></Menu.Item>
    </>
  );
}

function MemberServicesItems() {
  return (
    <>
    </>
  );
}

function WorkCommItems() {
  const { pathname } = useLocation();
  const sw = (p) => pathname.startsWith(p);
  return (
    <>
      {localStorage.getItem('staffId') && (
        <Menu.Item key="staff-chat" icon={<MessageOutlined />} style={sw('/staff-chat') ? activeItemStyle : {}}>
          <NavLink to="/staff-chat">Staff Chat</NavLink>
        </Menu.Item>
      )}
    </>
  );
}

function FormsItems() {
  const { pathname } = useLocation();
  const sw = (p) => pathname.startsWith(p);
  return (
    <>
      {hasPermission('/announced', 'readPermission') && <Menu.Item key="announced" icon={<SolutionOutlined />} style={sw('/announced') ? activeItemStyle : {}}><NavLink to="/announced">Announced Form</NavLink></Menu.Item>}
    </>
  );
}

// ─── Custom accordion group — avoids Ant Design SubMenu/openKeys issues ──────
function AdminGroup({ title, icon, paths, children }) {
  const { pathname } = useLocation();
  const isActive = paths.some(p => pathname.startsWith(p));
  const [open, setOpen] = useState(isActive);
  useEffect(() => { if (isActive) setOpen(true); }, [pathname]); // eslint-disable-line
  return (
    <div>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          height: 42, padding: '0 16px', margin: '2px 8px',
          borderRadius: 8, cursor: 'pointer', userSelect: 'none',
          color: isActive ? '#FFD700' : 'rgba(255,255,255,0.75)',
          background: isActive ? 'rgba(255,215,0,0.12)' : 'transparent',
          fontWeight: isActive ? 700 : 500, fontSize: 13.5,
          transition: 'background 0.15s',
        }}
      >
        <span style={{ fontSize: 15, display: 'flex', color: isActive ? '#fff' : 'rgba(255,255,255,0.6)' }}>{icon}</span>
        <span style={{ flex: 1 }}>{title}</span>
        <span style={{ fontSize: 10, opacity: 0.5 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ background: 'rgba(0,0,0,0.15)', borderRadius: 6, margin: '0 8px 4px' }}>
          {children}
        </div>
      )}
    </div>
  );
}

function AdminNavItem({ to, icon, label }) {
  const { pathname } = useLocation();
  const isActive = pathname.startsWith(to);
  return (
    <NavLink to={to} style={{ display: 'block', textDecoration: 'none' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        height: 38, padding: '0 14px 0 20px', fontSize: 13,
        color: isActive ? '#FFD700' : 'rgba(255,255,255,0.65)',
        background: isActive ? 'rgba(255,215,0,0.12)' : 'transparent',
        borderLeft: isActive ? '3px solid #FFD700' : '3px solid transparent',
        fontWeight: isActive ? 700 : 400,
        transition: 'background 0.15s',
      }}>
        <span style={{ fontSize: 14, display: 'flex' }}>{icon}</span>
        {label}
      </div>
    </NavLink>
  );
}

function CommunityItems() {
  return (
    <>
      {/* ── User Portal ── */}
      <AdminGroup title="User Portal" icon={<MobileOutlined />}
        paths={['/portal-updates', '/admin-notifications', '/community-board', '/admin-status', '/daily-quotes', '/monthly-reports', '/music-library', '/gallery']}>
        <AdminNavItem to="/portal-updates"      icon={<ContainerOutlined />}    label="Portal Updates" />
        <AdminNavItem to="/admin-notifications" icon={<NotificationOutlined />}  label="Notifications" />
        <AdminNavItem to="/community-board"     icon={<ReadOutlined />}          label="Community Posts" />
        <AdminNavItem to="/monthly-reports"     icon={<FileTextOutlined />}      label="Monthly Reports" />
        <AdminNavItem to="/admin-status"        icon={<PictureOutlined />}       label="Status Photos" />
        <AdminNavItem to="/daily-quotes"        icon={<BulbOutlined />}          label="Daily Quotes" />
        <AdminNavItem to="/music-library"       icon={<CustomerServiceOutlined />} label="Music Library & Radio" />
        <AdminNavItem to="/gallery"             icon={<PictureOutlined />}       label="Gallery (Drive Media)" />
      </AdminGroup>

      {/* ── Community Services ── */}
      <AdminGroup title="Community Services" icon={<TeamOutlined />}
        paths={['/gotra-directory','/jobs-admin','/vivah-admin','/samaj-parichay','/samaj-properties','/sammelan','/gram-pramukh','/vishesh-sahyogi','/comp-exam','/student-support','/inquiry','/contributions','/festivals']}>
        <AdminNavItem to="/gotra-directory"   icon={<ApartmentOutlined />}       label="Gotra Directory" />
        <AdminNavItem to="/jobs-admin"        icon={<SolutionOutlined />}        label="Rozgar Setu (Jobs)" />
        <AdminNavItem to="/vivah-admin"       icon={<HeartOutlined />}           label="Vivah Parichay" />
        <AdminNavItem to="/samaj-parichay"    icon={<BookOutlined />}            label="Samaj Parichay" />
        <AdminNavItem to="/samaj-properties"  icon={<BankOutlined />}            label="Bhawan / Gurukul" />
        <AdminNavItem to="/sammelan"          icon={<CalendarOutlined />}        label="Sammelan Events" />
        <AdminNavItem to="/gram-pramukh"      icon={<HomeOutlined />}            label="Gram Pramukh" />
        <AdminNavItem to="/vishesh-sahyogi"   icon={<TrophyOutlined />}          label="Vishesh Sahyogi" />
        <AdminNavItem to="/comp-exam-results" icon={<CheckSquareOutlined />}     label="Comp. Exam Results" />
        <AdminNavItem to="/student-support"   icon={<BookOutlined />}            label="Student Support (शिक्षा सहायता)" />
        <AdminNavItem to="/inquiry"           icon={<CustomerServiceOutlined />} label="Inquiries & Grievances" />
        <AdminNavItem to="/contributions"     icon={<HeartOutlined />}           label="Samajik Yogdan" />
        <AdminNavItem to="/festivals"         icon={<CalendarOutlined />}        label="Festival Celebrations" />
      </AdminGroup>
    </>
  );
}

function AdminItems() {
  return (
    <>
      {/* ── Finance ── */}
      <AdminGroup title="Finance" icon={<PieChartOutlined />}
        paths={['/expenditure-mgmt', '/financial-reports']}>
        <AdminNavItem to="/expenditure-mgmt"  icon={<span style={{ fontWeight: 800, fontSize: 13 }}>₹</span>} label="Expenditure Mgmt" />
        <AdminNavItem to="/financial-reports" icon={<PieChartOutlined />} label="Financial Reports" />
      </AdminGroup>

      {/* ── Communication ── */}
      <AdminGroup title="Communication" icon={<BellOutlined />}
        paths={['/message-log']}>
        <AdminNavItem to="/message-log" icon={<BellOutlined />} label="Message Logs" />
      </AdminGroup>

      {/* ── HR & Staff ── */}
      <AdminGroup title="HR & Staff" icon={<IdcardOutlined />}
        paths={['/staff-role', '/role', '/staff-tracking']}>
        <AdminNavItem to="/staff-role"           icon={<IdcardOutlined />}     label="Employee & Role" />
        <AdminNavItem to="/staff-tracking"       icon={<ClockCircleOutlined />} label="My Work Tracking" />
        <AdminNavItem to="/staff-tracking/admin" icon={<TeamOutlined />}       label="Staff Tracking" />
      </AdminGroup>

    </>
  );
}

function SettingsItems() {
  const { pathname } = useLocation();
  const sw = (p) => pathname.startsWith(p);
  return (
    <>
      {hasPermission('/receipt-template', 'readPermission') && <Menu.Item key="receipt-template"  icon={<FileImageOutlined />}   style={sw('/receipt-template')  ? activeItemStyle : {}}><NavLink to="/receipt-template">Receipt Template</NavLink></Menu.Item>}
      {hasPermission('/bank-account',     'readPermission') && <Menu.Item key="bank-account"      icon={<BankOutlined />}        style={sw('/bank-account')      ? activeItemStyle : {}}><NavLink to="/bank-account">Bank Accounts</NavLink></Menu.Item>}
      {hasPermission('/special-occasion', 'readPermission') && <Menu.Item key="special-occasion"  icon={<StarOutlined />}        style={sw('/special-occasion')  ? activeItemStyle : {}}><NavLink to="/special-occasion">Special Occasions</NavLink></Menu.Item>}
      {hasPermission('/village',          'readPermission') && <Menu.Item key="village"           icon={<EnvironmentOutlined />} style={sw('/village')           ? activeItemStyle : {}}><NavLink to="/village">Village Master</NavLink></Menu.Item>}
      {hasPermission('/app-settings',     'readPermission') && <Menu.Item key="app-settings"      icon={<SettingOutlined />}     style={sw('/app-settings')      ? activeItemStyle : {}}><NavLink to="/app-settings">API Settings</NavLink></Menu.Item>}
    </>
  );
}

function BusinessDirectoryItems() {
  const { pathname } = useLocation();
  const sw = (p) => pathname.startsWith(p);
  return (
    <>
      {hasPermission('/business-directory/sectors', 'readPermission') && (
        <Menu.Item key="bd-sectors" icon={<UnorderedListOutlined />} style={sw('/business-directory/sectors') && !sw('/business-directory/config') ? activeItemStyle : {}}>
          <NavLink to="/business-directory/sectors">Sector Management</NavLink>
        </Menu.Item>
      )}
      {hasPermission('/business-directory', 'readPermission') && (
        <Menu.Item key="bd-list" icon={<ShopOutlined />} style={pathname === '/business-directory' ? activeItemStyle : {}}>
          <NavLink to="/business-directory">Submissions</NavLink>
        </Menu.Item>
      )}
      {hasPermission('/business-directory/sectors', 'editPermission') && (
        <Menu.Item key="bd-config" icon={<SlidersOutlined />} style={sw('/business-directory/config') ? activeItemStyle : {}}>
          <NavLink to="/business-directory/config">Form Settings</NavLink>
        </Menu.Item>
      )}
      {hasPermission('/business-directory', 'readPermission') && (
        <Menu.Item key="bd-mobile" icon={<MobileOutlined style={{ color: '#d4a84b' }} />}
          style={sw('/directory-mobile') ? activeItemStyle : { background: 'rgba(212,168,75,0.08)', borderLeft: '3px solid #d4a84b', marginTop: 4 }}>
          <NavLink to="/directory-mobile" style={{ color: '#d4a84b', fontWeight: 700 }}>
            Mobile View
          </NavLink>
        </Menu.Item>
      )}
    </>
  );
}

function PopulationItems() {
  const { pathname } = useLocation();
  const sw = (p) => pathname.startsWith(p);
  return (
    <>
      <Menu.Item key="pop-dashboard"     icon={<DashboardOutlined />}    style={sw('/pop-dashboard')     ? activeItemStyle : {}}><NavLink to="/pop-dashboard">Dashboard</NavLink></Menu.Item>
      <Menu.Item key="pop-families"      icon={<TeamOutlined />}         style={sw('/pop-families') && !sw('/pop-family-member') ? activeItemStyle : {}}><NavLink to="/pop-families">Families</NavLink></Menu.Item>
      <Menu.Item key="pop-family-member" icon={<UserSwitchOutlined />}   style={sw('/pop-family-member') ? activeItemStyle : {}}><NavLink to="/pop-family-member">Family Members</NavLink></Menu.Item>
      <Menu.Item key="pop-country"       icon={<GlobalOutlined />}       style={sw('/pop-country')       ? activeItemStyle : {}}><NavLink to="/pop-country">Country</NavLink></Menu.Item>
      <Menu.Item key="pop-states"        icon={<EnvironmentOutlined />}  style={sw('/pop-states')        ? activeItemStyle : {}}><NavLink to="/pop-states">States</NavLink></Menu.Item>
      <Menu.Item key="pop-districts"     icon={<EnvironmentOutlined />}  style={sw('/pop-districts')     ? activeItemStyle : {}}><NavLink to="/pop-districts">Districts</NavLink></Menu.Item>
      <Menu.Item key="pop-tehsils"       icon={<EnvironmentOutlined />}  style={sw('/pop-tehsils')       ? activeItemStyle : {}}><NavLink to="/pop-tehsils">Tehsils</NavLink></Menu.Item>
      <Menu.Item key="pop-panchayat"     icon={<HomeOutlined />}         style={sw('/pop-panchayat')     ? activeItemStyle : {}}><NavLink to="/pop-panchayat">Panchayat</NavLink></Menu.Item>
      <Menu.Item key="pop-village"       icon={<HomeOutlined />}         style={sw('/pop-village')       ? activeItemStyle : {}}><NavLink to="/pop-village">Village</NavLink></Menu.Item>
      <Menu.Item key="pop-education"     icon={<BookOutlined />}         style={sw('/pop-education')     ? activeItemStyle : {}}><NavLink to="/pop-education">Education</NavLink></Menu.Item>
      <Menu.Item key="pop-occupations"   icon={<SolutionOutlined />}     style={sw('/pop-occupations')   ? activeItemStyle : {}}><NavLink to="/pop-occupations">Occupations</NavLink></Menu.Item>
    </>
  );
}

// ─── Group-to-renderer map ────────────────────────────────────────────────────
const GROUP_RENDERERS = {
  reports:              <ReportsItems />,
  'member-services':    <MemberServicesItems />,
  'work-comm':          <WorkCommItems />,
  forms:                <FormsItems />,
  community:            <CommunityItems />,
  administration:       <AdminItems />,
  settings:             <SettingsItems />,
  'data-directory':     null,
  population:           <PopulationItems />,
  'business-directory': <BusinessDirectoryItems />,
};

// ─── Component ────────────────────────────────────────────────────────────────
// Derive which single submenu should be auto-open based on current route
function getInitialOpenKey(pathname) {
  // Financial expenditure sub
  if (
    pathname.includes('/expenditure') || pathname.includes('/beneficiaries') ||
    pathname.includes('/purposes')    || pathname.includes('/budget-alloc')  ||
    pathname.includes('/approval-workflow')
  ) return 'expenditure-sub';
  if (
    pathname.includes('/reports/expenditure') ||
    pathname.includes('/reports/budget')      ||
    pathname.includes('/reports/approval')    ||
    pathname.includes('/monthly-reports')
  ) return 'financial-reports-sub';

  // Community tab SubMenus
  const portalPaths    = ['/portal-updates', '/admin-notifications', '/community-board', '/admin-status', '/daily-quotes', '/monthly-reports', '/music-library', '/gallery'];
  const communityPaths = ['/gotra-directory', '/jobs-admin', '/vivah-admin', '/samaj-parichay', '/samaj-properties', '/sammelan', '/gram-pramukh', '/vishesh-sahyogi', '/comp-exam', '/student-support', '/inquiry', '/contributions', '/festivals'];

  if (portalPaths.some(p => pathname.startsWith(p)))    return 'admin-portal-sub';
  if (communityPaths.some(p => pathname.startsWith(p))) return 'admin-community-sub';

  // Manage tab SubMenus
  const financePaths   = ['/expenditure-mgmt', '/financial-reports'];
  const commsPaths     = ['/message-log'];
  const hrPaths        = ['/staff-role', '/role', '/staff-tracking'];

  if (financePaths.some(p => pathname.startsWith(p)))   return 'admin-finance-sub';
  if (commsPaths.some(p => pathname.startsWith(p)))     return 'admin-comms-sub';
  if (hrPaths.some(p => pathname.startsWith(p)))        return 'admin-hr-sub';

  return null;
}

function Sidenav({ activeGroup, color, showAll = false, collapsed = false }) {
  const { pathname } = useLocation();

  const selectedKeys = [getSelectedKey(pathname)].filter(Boolean);

  // Controlled accordion: only one submenu open at a time
  const [openKeys, setOpenKeys] = useState(() => {
    const k = getInitialOpenKey(pathname);
    return k ? [k] : [];
  });

  // When route changes (e.g. navigating to an expenditure page), auto-open correct submenu
  useEffect(() => {
    const k = getInitialOpenKey(pathname);
    if (k) setOpenKeys([k]);
  }, [pathname]);

  const handleOpenChange = (keys) => {
    // Accordion: keep only the last opened key
    const latest = keys.find(k => !openKeys.includes(k));
    setOpenKeys(latest ? [latest] : []);
  };

  const accentColor = color || '#1890ff';

  // ── Mobile "show all" mode ────────────────────────────────
  if (showAll) {
    return (
      <div style={{ paddingBottom: 24 }}>
        <MobileSectionLabel>
          <DashboardOutlined className="sec-icon" />
          Quick Access
        </MobileSectionLabel>
        <StyledMenu theme="light" mode="inline" selectedKeys={selectedKeys}>
          {hasPermission('/dashboard',                'readPermission')   && <Menu.Item key="m-dashboard"  icon={<DashboardOutlined />}><NavLink to="/dashboard">Dashboard</NavLink></Menu.Item>}
          {hasPermission('/payments/add_user_payment','createPermission') && <Menu.Item key="m-addpay"     icon={<PlusCircleOutlined />}><NavLink to="/payments/add_user_payment">Add Payment</NavLink></Menu.Item>}
          {hasPermission('/users',                    'readPermission')   && <Menu.Item key="m-members"    icon={<TeamOutlined />}><NavLink to="/users">Members</NavLink></Menu.Item>}
          {hasPermission('/payments',                 'readPermission')   && <Menu.Item key="m-transaction" icon={<FundOutlined />}><NavLink to="/payments">Transaction</NavLink></Menu.Item>}
        </StyledMenu>

        {Object.entries(GROUP_RENDERERS).map(([groupKey, items]) => {
          const meta = GROUP_META[groupKey];
          const SecIcon = meta.icon;
          const isCustomAccordion = groupKey === 'administration' || groupKey === 'community';
          return (
            <div key={groupKey}>
              <MobileSectionLabel>
                <SecIcon className="sec-icon" />
                {meta.title}
              </MobileSectionLabel>
              {isCustomAccordion ? (
                <div style={{ paddingBottom: 4 }}>{items}</div>
              ) : (
                <StyledMenu
                  theme="light"
                  mode="inline"
                  selectedKeys={selectedKeys}
                  openKeys={collapsed ? [] : openKeys}
                  onOpenChange={handleOpenChange}
                  inlineIndent={12}
                >
                  {items}
                </StyledMenu>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ── Desktop contextual mode ───────────────────────────────
  const OPERATIONS_GROUPS     = ['reports'];
  const ADMINISTRATION_GROUPS = ['administration', 'settings'];
  const DIRECTORY_GROUPS      = ['business-directory', 'population'];

  const isOperations     = OPERATIONS_GROUPS.includes(activeGroup);
  const isAdministration = ADMINISTRATION_GROUPS.includes(activeGroup);
  const isDirectory      = activeGroup === 'data-directory';

  const groupsToShow = isOperations
    ? OPERATIONS_GROUPS
    : isAdministration
      ? ADMINISTRATION_GROUPS
      : isDirectory
        ? DIRECTORY_GROUPS
        : [activeGroup];

  if (!GROUP_RENDERERS[groupsToShow[0]] && !isDirectory) return null;

  return (
    <div style={{ paddingBottom: 16 }}>
      {groupsToShow.map(groupKey => {
        const meta    = GROUP_META[groupKey];
        const SecIcon = meta?.icon;
        const items   = GROUP_RENDERERS[groupKey];
        if (!items) return null;
        // 'administration' uses custom accordion (not Ant Design SubMenu)
        const isCustomAccordion = groupKey === 'administration' || groupKey === 'community';
        return (
          <div key={groupKey}>
            {!collapsed && (
              <SectionHeader $color={accentColor}>
                {SecIcon && <SecIcon className="sec-icon" />}
                {meta?.title}
              </SectionHeader>
            )}
            {isCustomAccordion ? (
              <div style={{ paddingBottom: 4 }}>{items}</div>
            ) : (
              <StyledMenu
                theme="light"
                mode="inline"
                selectedKeys={selectedKeys}
                openKeys={collapsed ? [] : openKeys}
                onOpenChange={handleOpenChange}
                inlineCollapsed={collapsed}
              >
                {items}
              </StyledMenu>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default Sidenav;
